import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { formatDateDDMonYY } from "@/utils/dateFormat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  Search, 
  MessageCircle,
  Send,
  Phone,
  User,
  Clock,
  RefreshCw
} from "lucide-react";

export default function Conversations() {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedConv?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
      if (response.data.length > 0 && !selectedConv) {
        fetchConversationDetails(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationDetails = async (convId) => {
    try {
      const response = await axios.get(`${API}/conversations/${convId}`);
      setSelectedConv(response.data);
    } catch (error) {
      toast.error("Failed to fetch conversation");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConv) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${selectedConv.id}/send?message=${encodeURIComponent(newMessage)}`);
      setNewMessage("");
      fetchConversationDetails(selectedConv.id);
      toast.success("Message sent");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.customer_phone.includes(searchTerm) ||
    conv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return formatDateDDMonYY(date);
  };

  const getLastMessage = (conv) => {
    if (!conv.messages || conv.messages.length === 0) {
      return "No messages yet";
    }
    const lastMsg = conv.messages[conv.messages.length - 1];
    return lastMsg.content.length > 40 
      ? lastMsg.content.substring(0, 40) + "..." 
      : lastMsg.content;
  };

  return (
    <div className="p-8 h-[calc(100vh-2rem)]" data-testid="conversations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Conversations
          </h1>
          <p className="text-muted-foreground mt-1">
            WhatsApp chat history with customers
          </p>
        </div>
        <Button variant="outline" onClick={fetchConversations} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      <div className="flex gap-6 h-[calc(100%-100px)]">
        {/* Conversation List */}
        <Card className="w-1/3 min-w-[300px] border-border/60 flex flex-col">
          <CardHeader className="py-4 px-4 border-b border-border/60">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-conversations-input"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Loading conversations...</p>
              </div>
            ) : filteredConversations.length > 0 ? (
              <ScrollArea className="h-full">
                {filteredConversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`p-4 cursor-pointer transition-all border-b border-border/40 hover:bg-secondary/50 ${
                      selectedConv?.id === conv.id ? 'bg-secondary' : ''
                    }`}
                    onClick={() => fetchConversationDetails(conv.id)}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium truncate">
                            {conv.customer_name || conv.customer_phone}
                          </p>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(conv.updated_at)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground truncate mt-1">
                          {getLastMessage(conv)}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge 
                            variant="outline" 
                            className={conv.status === 'active' ? 'border-green-300 text-green-700' : ''}
                          >
                            {conv.status}
                          </Badge>
                          {conv.messages?.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {conv.messages.length} messages
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            ) : (
              <div className="empty-state h-full">
                <MessageCircle className="w-12 h-12 mb-4 opacity-30" />
                <p>No conversations yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Conversations will appear when customers message via WhatsApp
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="flex-1 border-border/60 flex flex-col overflow-hidden">
          {selectedConv ? (
            <>
              {/* Chat Header */}
              <CardHeader className="py-4 px-6 border-b border-border/60 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                        {selectedConv.customer_name || "New Customer"}
                      </CardTitle>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="w-3 h-3" />
                        {selectedConv.customer_phone}
                      </div>
                    </div>
                  </div>
                  <Badge 
                    variant="outline"
                    className={selectedConv.status === 'active' ? 'border-green-300 text-green-700' : ''}
                  >
                    {selectedConv.status}
                  </Badge>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full">
                  <div className="p-6 space-y-4 whatsapp-bg min-h-full">
                    {selectedConv.messages?.length > 0 ? (
                      selectedConv.messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'} message-enter`}
                          style={{ animationDelay: `${idx * 0.05}s` }}
                        >
                          <div
                            className={msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-assistant'}
                          >
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <div className="flex items-center justify-end gap-1 mt-1">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-400">
                                {formatTime(msg.timestamp)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <p>No messages in this conversation</p>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              {/* Message Input */}
              <div className="p-4 border-t border-border/60 flex-shrink-0 bg-white">
                <form onSubmit={handleSendMessage} className="flex gap-3">
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-1"
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm">Choose a conversation from the list to view messages</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
